import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { EstimateStats } from '../components/estimates/EstimateStats';
import { EstimateList } from '../components/estimates/EstimateList';
import { EstimateForm } from '../components/estimates/EstimateForm';
import { EstimateDetail } from '../components/estimates/EstimateDetail';
import { estimateService } from '../services/estimateService';
import { EstimateFilters, CreateEstimateData, UpdateEstimateData, Estimate } from '../types/estimate';

type PageMode = 'list' | 'create' | 'edit' | 'view';

export const EstimatesPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [mode, setMode] = useState<PageMode>('list');
  const [selectedEstimate, setSelectedEstimate] = useState<Estimate | null>(null);

  const { data: estimateData } = useQuery({
    queryKey: ['estimate', id],
    queryFn: () => estimateService.getEstimate(id!),
    enabled: !!id && mode === 'list',
  });

  useEffect(() => {
    if (id && estimateData) {
      setSelectedEstimate(estimateData);
      setMode('view');
    } else if (!id) {
      setMode('list');
      setSelectedEstimate(null);
    }
  }, [id, estimateData]);

  const [filters, setFilters] = useState<EstimateFilters>({ page: 1, limit: 10 });

  const queryClient = useQueryClient();

  const createMutation = useMutation({
    mutationFn: estimateService.createEstimate,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['estimates'] });
      queryClient.invalidateQueries({ queryKey: ['estimate-stats'] });
      setMode('list');
    },
    onError: (error) => { console.error('Failed to create estimate:', error); }
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateEstimateData }) =>
      estimateService.updateEstimate(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['estimates'] });
      queryClient.invalidateQueries({ queryKey: ['estimate-stats'] });
      queryClient.invalidateQueries({ queryKey: ['estimate', selectedEstimate?.id] });
      setMode('list');
      setSelectedEstimate(null);
    },
    onError: (error) => { console.error('Failed to update estimate:', error); }
  });

  const handleCreateEstimate = (data: CreateEstimateData) => { createMutation.mutate(data); };
  const handleUpdateEstimate = (data: CreateEstimateData) => {
    if (selectedEstimate) {
      updateMutation.mutate({ id: selectedEstimate.id, data: { ...data, items: data.items } });
    }
  };
  const handleViewEstimate = (estimate: Estimate) => { setSelectedEstimate(estimate); setMode('view'); };
  const handleEditEstimate = (estimate: Estimate) => { setSelectedEstimate(estimate); setMode('edit'); };
  const handleBackToList = () => { navigate('/estimates'); setMode('list'); setSelectedEstimate(null); };
  const handleFilterChange = (newFilters: Partial<EstimateFilters>) => {
    setFilters(prev => ({ ...prev, ...newFilters, page: 1 }));
  };

  if (mode === 'create') {
    return <div style={{ padding: 24 }}><EstimateForm onSubmit={handleCreateEstimate} onCancel={handleBackToList} /></div>;
  }
  if (mode === 'edit' && selectedEstimate) {
    return <div style={{ padding: 24 }}><EstimateForm estimate={selectedEstimate} onSubmit={handleUpdateEstimate} onCancel={handleBackToList} /></div>;
  }
  if (mode === 'view' && selectedEstimate) {
    return <EstimateDetail estimateId={selectedEstimate.id} onEdit={handleEditEstimate} onBack={handleBackToList} />;
  }

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '9px 12px',
    background: '#f4efe2',
    border: '1px solid rgba(92,78,51,0.25)',
    borderRadius: 4,
    fontFamily: "'Inter', sans-serif",
    fontSize: 12,
    color: '#5b5a52',
    outline: 'none',
    boxSizing: 'border-box',
  };

  const labelStyle: React.CSSProperties = {
    fontFamily: "'Inter', sans-serif",
    fontSize: 9,
    fontWeight: 500,
    letterSpacing: '0.2em',
    textTransform: 'uppercase',
    color: '#6b6a5c',
    display: 'block',
    marginBottom: 6,
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      {/* Page header */}
      <div style={{ paddingBottom: 20, borderBottom: '1px solid rgba(92,78,51,0.15)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
          <div>
            <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 10, fontWeight: 400, letterSpacing: '0.4em', textTransform: 'uppercase', color: '#8a8778', marginBottom: 8 }}>
              Sales
            </div>
            <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 36, fontWeight: 300, color: '#1c2033', lineHeight: 1 }}>
              Estimates
            </div>
            <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 13, fontWeight: 300, color: '#8a8778', marginTop: 8 }}>
              Manage your estimates and quotes
            </div>
          </div>
          <button
            onClick={() => setMode('create')}
            style={{
              fontFamily: "'Inter', sans-serif",
              fontSize: 10,
              fontWeight: 500,
              letterSpacing: '0.2em',
              textTransform: 'uppercase',
              background: 'linear-gradient(135deg, #d97706, #b45309)',
              color: '#1c2033',
              border: 'none',
              padding: '12px 24px',
              borderRadius: 4,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              transition: 'all 0.2s',
            }}
            onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.boxShadow = '0 8px 32px rgba(217,119,6,0.25)'; }}
            onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.boxShadow = 'none'; }}
          >
            <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Create Estimate
          </button>
        </div>
      </div>

      {/* Stats */}
      <EstimateStats />

      {/* Filters */}
      <div style={{ background: '#ffffff', border: '1px solid rgba(92,78,51,0.15)', borderRadius: 6, padding: 20 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr auto', gap: 16 }}>
          <div>
            <label style={labelStyle}>Search</label>
            <input
              type="text"
              placeholder="Search estimates..."
              value={filters.search || ''}
              onChange={(e) => handleFilterChange({ search: e.target.value })}
              style={inputStyle}
              onFocus={e => { (e.target as HTMLInputElement).style.borderColor = 'rgba(217,119,6,0.35)'; }}
              onBlur={e => { (e.target as HTMLInputElement).style.borderColor = 'rgba(92,78,51,0.25)'; }}
            />
          </div>

          <div>
            <label style={labelStyle}>Status</label>
            <select
              value={filters.status || ''}
              onChange={(e) => handleFilterChange({ status: e.target.value as any })}
              style={{ ...inputStyle, cursor: 'pointer' }}
            >
              <option value="">All Statuses</option>
              <option value="DRAFT">Draft</option>
              <option value="SENT">Sent</option>
              <option value="ACCEPTED">Accepted</option>
              <option value="DECLINED">Declined</option>
              <option value="EXPIRED">Expired</option>
            </select>
          </div>

          <div>
            <label style={labelStyle}>Items per page</label>
            <select
              value={filters.limit}
              onChange={(e) => handleFilterChange({ limit: parseInt(e.target.value) })}
              style={{ ...inputStyle, cursor: 'pointer' }}
            >
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
            </select>
          </div>

          <div style={{ display: 'flex', alignItems: 'flex-end' }}>
            <button
              onClick={() => setFilters({ page: 1, limit: 10 })}
              style={{
                fontFamily: "'Inter', sans-serif",
                fontSize: 9,
                fontWeight: 500,
                letterSpacing: '0.2em',
                textTransform: 'uppercase',
                color: '#6b6a5c',
                background: 'rgba(239,232,213,0.5)',
                border: '1px solid rgba(92,78,51,0.22)',
                borderRadius: 4,
                padding: '9px 16px',
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                transition: 'all 0.2s',
              }}
              onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.color = '#d97706'; (e.currentTarget as HTMLButtonElement).style.borderColor = 'rgba(217,119,6,0.25)'; }}
              onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.color = '#8a8778'; (e.currentTarget as HTMLButtonElement).style.borderColor = 'rgba(92,78,51,0.22)'; }}
            >
              Clear Filters
            </button>
          </div>
        </div>
      </div>

      <EstimateList
        filters={filters}
        onView={handleViewEstimate}
        onEdit={handleEditEstimate}
        onCreateNew={() => setMode('create')}
      />
    </div>
  );
};
